import axios from "axios";

import { getApiConfig } from "@/config/api.config";

// Types
interface InventoryItem {
  id: string;
  storeId: number;
  skuId: string;
  productId: string;
  title: string;
  imageUrl: string;
  url: string;
  description: string;
  brand: string;
  color: string;
  size: string;
  sizeLabel: string;
  size2?: string;
  size2Label?: string;
  quantity: number;
  price: string;
  salePrice: string | null;
  trending: boolean;
  colorCode: string;
  itemId: string;
  category: string;
  colorFamily: string;
  inStoreQuantity: number;
  gtin?: string;
  promotion?: string | null;
}

interface GroupedInventoryItem {
  productId: string;
  title: string;
  imageUrl: string;
  url: string;
  description: string;
  brand: string;
  category: string;
  variants: InventoryItem[];
  totalQuantity: number;
  price: string;
  salePrice: string | null;
}

interface PaginatedInventoryResponse {
  items: GroupedInventoryItem[];
  hasMore: boolean;
  total: number | null;
}

interface ProductDetailsResponse {
  data: {
    skus: InventoryItem[];
    size: {
      label: Record<string, string>;
      values: string[];
    };
    size2: {
      label: Record<string, string>;
      values: string[];
    };
    color: {
      label: Record<string, string>;
      values: string[];
    };
  };
  metadata: {
    code: number;
    message: string;
    errors: any[] | null;
    warnings: any[] | null;
  };
}

class InventoryService {
  private baseUrl: string;
  private pageSize = 50;
  private authToken: string;

  constructor(authToken: string) {
    this.baseUrl = getApiConfig().inventory;
    this.authToken = authToken;
  }

  /**
   * Returns the base URL for the inventory service
   * @returns The base URL string
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Fetches inventory data for a specific store with pagination
   * @param storeId The ID of the store
   * @param pageNumber The page number to fetch (1-based)
   * @returns Promise with the inventory items
   */
  async fetchInventoryPage(storeId: number, pageNumber: number): Promise<InventoryItem[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/json/sample/${storeId}`, {
        params: { pageNumber },
        headers: {
          Authorization: `Bearer ${this.authToken}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Groups inventory items by productId
   * @param items Array of inventory items
   * @returns Array of grouped inventory items
   */
  groupInventoryByProduct(items: InventoryItem[]): GroupedInventoryItem[] {
    const groupedMap = new Map<string, GroupedInventoryItem>();

    // First, sort items by productId to ensure consistent grouping
    const sortedItems = [...items].sort((a, b) => a.productId.localeCompare(b.productId));

    sortedItems.forEach((item) => {
      if (!groupedMap.has(item.productId)) {
        // Create a new group for this productId
        groupedMap.set(item.productId, {
          productId: item.productId,
          title: item.title,
          imageUrl: item.imageUrl,
          url: item.url,
          description: item.description,
          brand: item.brand,
          category: item.category,
          variants: [],
          totalQuantity: 0,
          price: item.price,
          salePrice: item.salePrice,
        });
      }

      const group = groupedMap.get(item.productId)!;

      // Check if we already have a variant with the same SKU
      const existingVariantIndex = group.variants.findIndex(
        (variant) => variant.skuId === item.skuId,
      );

      if (existingVariantIndex === -1) {
        // This is a new variant
        group.variants.push(item);
      } else {
        // Update existing variant's quantity
        group.variants[existingVariantIndex].quantity += item.quantity;
      }

      group.totalQuantity += item.quantity;
    });

    // Convert the Map to an array and sort by title
    return Array.from(groupedMap.values()).sort((a, b) => a.title.localeCompare(b.title));
  }

  /**
   * Loads a single page of inventory data
   * @param storeId The ID of the store
   * @param pageNumber The page number to fetch (1-based)
   * @returns Promise with paginated and grouped inventory items
   */
  async loadInventoryPage(
    storeId: number,
    pageNumber: number,
  ): Promise<PaginatedInventoryResponse> {
    const items = await this.fetchInventoryPage(storeId, pageNumber);
    const hasMore = items.length === this.pageSize;
    const groupedItems = this.groupInventoryByProduct(items);

    return {
      items: groupedItems,
      hasMore,
      total: null, // The API doesn't provide a total count
    };
  }

  /**
   * Fetches detailed product information by store ID and SKU ID
   * @param storeId The ID of the store
   * @param skuId The SKU ID of the product
   * @returns Promise with the product details
   */
  async fetchProductDetails(storeId: number, skuId: string): Promise<ProductDetailsResponse> {
    try {
      const response = await axios.get(`${this.baseUrl}/v2/product/store/${storeId}/sku/${skuId}`, {
        headers: {
          Authorization: `Bearer ${this.authToken}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches SKU information by store ID and SKU ID
   * @param storeId The ID of the store
   * @param skuId The SKU ID of the product
   * @returns Promise with the SKU details
   */
  async getSkuBySkuId(storeId: number, skuId: string): Promise<InventoryItem> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/v2/inventory/store/${storeId}/sku/${skuId}`,
        {
          headers: {
            Authorization: `Bearer ${this.authToken}`,
          },
        },
      );
      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Parses an EPC for a store and returns the matching SKU.
   * @param storeId The ID of the store
   * @param epc The EPC value
   * @returns Promise with the SKU details
   */
  async parseEpc(storeId: number, epc: string): Promise<InventoryItem> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/v2/inventory/store/${storeId}/epc/${encodeURIComponent(epc)}`,
        {
          headers: {
            Authorization: `Bearer ${this.authToken}`,
          },
        },
      );
      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  async getFirstSkuByProductId(storeId: number, productId: string): Promise<InventoryItem> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/product/store/${storeId}/product/${encodeURIComponent(productId)}/first`,
        {
          headers: {
            Authorization: `Bearer ${this.authToken}`,
          },
        },
      );
      return response.data.data;
    } catch (error) {
      throw error;
    }
  }
}

export default InventoryService;
export type { GroupedInventoryItem, InventoryItem, ProductDetailsResponse };
